import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'

import { Switch } from '@gto/ui/switch'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAdminPartnerDetailSchema } from '@/api/gen'
import { MutationError } from '@/components/mutation-error'

import { DotStatus } from './dot-status'
import { RailSection } from './rail-section'
import { usePartnerUpdate } from './use-partner-update'

type AccessSectionProps = {
  partner: Pick<
    AffiliateAdminPartnerDetailSchema,
    'can_access_portal' | 'is_paired' | 'fully_migrated' | 'terms_accepted_at'
  >
  partnerId: string
}

/**
 * Portal access + GTOW pairing, rendered in the rail next to StatusBadge and
 * the workflow action: both facts are partner STATE, not profile data, and
 * they use the same dot vocabulary as the partners list.
 */
export const AccessSection = ({ partner, partnerId }: AccessSectionProps) => {
  const { formatMessage } = useIntl()
  // Reuse the shared partner-update chokepoint (same cache invalidation + Sentry
  // sink as every other editable surface on this page). The two toggles each
  // get their OWN instance so a failure surfaces under the action that caused
  // it — a failed fully_migrated toggle must not read "Could not update portal
  // access" — and so an in-flight toggle doesn't disable the other one.
  const { mutation: portalMutation, hasError: portalHasError } = usePartnerUpdate(
    partnerId,
    () => undefined,
  )
  const { mutation: migratedMutation, hasError: migratedHasError } = usePartnerUpdate(
    partnerId,
    () => undefined,
  )

  return (
    <RailSection title={formatMessage({ id: 'admin.partnerDetail.access' })}>
      <div className="flex items-center justify-between gap-3">
        <Typography
          color="text.secondary"
          variant="caption"
        >
          <FormattedMessage id="admin.partnerDetail.portalAccess" />
        </Typography>
        <span className="inline-flex items-center gap-2">
          <Switch
            aria-label={formatMessage({ id: 'admin.partnerDetail.portalAccess.toggleLabel' })}
            checked={partner.can_access_portal}
            data-tst="portal-access-toggle"
            disabled={portalMutation.isPending}
            onCheckedChange={(checked) =>
              portalMutation.mutate({ partnerId, data: { can_access_portal: checked } })
            }
          />
          <DotStatus
            color={partner.can_access_portal ? 'success' : 'secondary'}
            label={
              <FormattedMessage
                id={
                  partner.can_access_portal
                    ? 'admin.partnerDetail.portalAccess.granted'
                    : 'admin.partnerDetail.portalAccess.locked'
                }
              />
            }
          />
        </span>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Typography
          color="text.secondary"
          variant="caption"
        >
          <FormattedMessage id="admin.partnerDetail.portalAccess.pairing" />
        </Typography>
        <DotStatus
          color={partner.is_paired ? 'success' : 'secondary'}
          label={
            <FormattedMessage
              id={
                partner.is_paired
                  ? 'admin.partnerDetail.portalAccess.paired'
                  : 'admin.partnerDetail.portalAccess.unpaired'
              }
            />
          }
        />
      </div>

      {/* Fully-migrated toggle: admin-confirmed reconciliation. Independent of
          `is_migrated` (legacy origin, BE-set) and of `can_access_portal`
          (beta gating). Uses its OWN usePartnerUpdate instance so a failure
          surfaces under THIS action (not the portal-access banner) and the
          two toggles don't disable together. */}
      <div className="flex items-center justify-between gap-3">
        <Typography
          color="text.secondary"
          variant="caption"
        >
          <FormattedMessage id="admin.partnerDetail.fullyMigrated" />
        </Typography>
        <span className="inline-flex items-center gap-2">
          <Switch
            aria-label={formatMessage({ id: 'admin.partnerDetail.fullyMigrated.toggleLabel' })}
            checked={partner.fully_migrated}
            data-tst="fully-migrated-toggle"
            disabled={migratedMutation.isPending}
            onCheckedChange={(checked) =>
              migratedMutation.mutate({ partnerId, data: { fully_migrated: checked } })
            }
          />
          <DotStatus
            color={partner.fully_migrated ? 'success' : 'secondary'}
            label={
              <FormattedMessage
                id={
                  partner.fully_migrated
                    ? 'admin.partnerDetail.fullyMigrated.on'
                    : 'admin.partnerDetail.fullyMigrated.off'
                }
              />
            }
          />
        </span>
      </div>

      <MutationError
        data-tst="fully-migrated-error"
        messageId="admin.partnerDetail.fullyMigrated.error"
        show={migratedHasError}
      />

      {/* T&C acceptance is partner-facing (read-only here): we only show
          when/whether they accepted, no toggle. Distinguishes "Accepted on
          <date>" from "Not accepted" so admins can see migration progress at
          a glance. */}
      <div className="flex items-center justify-between gap-3">
        <Typography
          color="text.secondary"
          variant="caption"
        >
          <FormattedMessage id="admin.partnerDetail.termsAccepted" />
        </Typography>
        <span
          className="inline-flex items-center gap-2"
          data-tst="terms-accepted"
        >
          {partner.terms_accepted_at == null ? (
            <DotStatus
              color="secondary"
              label={<FormattedMessage id="admin.partnerDetail.termsAccepted.notAccepted" />}
            />
          ) : (
            <DotStatus
              color="success"
              label={
                <FormattedMessage
                  id="admin.partnerDetail.termsAccepted.acceptedOn"
                  values={{
                    date: (
                      <FormattedDate
                        day="numeric"
                        month="short"
                        value={partner.terms_accepted_at}
                        year="numeric"
                      />
                    ),
                  }}
                />
              }
            />
          )}
        </span>
      </div>

      <Typography
        color="text.secondary"
        variant="caption"
      >
        <FormattedMessage id="admin.partnerDetail.portalAccess.toggleHint" />
      </Typography>

      <MutationError
        data-tst="portal-access-error"
        messageId="admin.partnerDetail.portalAccess.error"
        show={portalHasError}
      />
    </RailSection>
  )
}
